import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ProcessedAttribute } from '../utils/attributeValidation';
import { aiAttributeApi } from '../AiAttributesList/api/aiAttribute.api';
import { useShowToast } from '@/hooks/useShowToast';
import { useJobPipeline } from '@/hooks/useJobPipeline';
import { AiScoringConstants } from '../aiScoringConstants';
import { isEqual } from 'lodash-es';
import {
  useBulkOperationsOptional,
  useBulkOperation,
} from '@/components/ats/BulkOperationQueue';

const { STEP_ICP, STEP_ATTRIBUTES, STEP_TESTING } = AiScoringConstants;

// Scroll utility function for drawer content
const scrollToElementById = (
  elementId: string,
  toast,
  delay: number = 1500
) => {
  setTimeout(() => {
    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, delay);
};

interface UseAiScoringDrawerProps {
  icpData: { data: string; isModified: boolean; categories: string[] };
  setIcpData: React.Dispatch<
    React.SetStateAction<{
      data: string;
      categories: string[];
      isModified: boolean;
    }>
  >;
  getIcpCategoriesMutation: any;
  attributes: ProcessedAttribute[];
  isDirty: boolean;
  isValid: boolean;
  saveAttributes: (shouldSave?: boolean, retrigger?: boolean) => Promise<any>;
  resetDirtyState: () => void;
  attributesLoading: boolean;
  onClose: () => void;
  onValidationComplete?: (result: any) => void;
  hasSession?: boolean;
  jobData: { id: string; description?: { text: string | null } };
  isFromJobPipeline?: boolean;
  hasValidatedButNotSaved?: boolean;
  // saveIcpMutation: any; // Commented out - can be uncommented in future
  generateAttributesMutation: any;
  isIcpLoading: boolean;
  activeStep: number;
  setActiveStep: React.Dispatch<React.SetStateAction<number>>;
  generatedAttributes?: any[]; // Add this to track freshly generated attributes
  onSuccessfulUpdate?: () => void;
}

interface UseAiScoringDrawerReturn {
  // State
  footerIsLoading: boolean;
  footerLoadingKey: string | null;
  hasUnsavedGeneratedAttributes: boolean;
  shouldRerunAiTest: boolean;
  hasAttributesChangedSinceLastValidation: boolean;

  // Actions
  setFooterIsLoading: (loading: boolean) => void;
  setFooterLoadingKey: (key: string | null) => void;
  setShouldRerunAiTest: (should: boolean) => void;

  // Handlers
  handleStepChange: (index: number) => Promise<void>;
  handleCancel: () => void;
  handleBack: () => void;
  handleSave: (shouldRetrigger?: boolean) => Promise<void>;
  handleSkipAndSave: (shouldRetrigger?: boolean) => Promise<void>;
  handleVerifyIcp: () => Promise<void>;
  handleVerifyAttributes: () => Promise<void>;
  handleValidationComplete: (result: any) => void;

  // Utilities
  scrollToElementById: (elementId: string, toast: any, delay?: number) => void;
}

export const useAiScoringDrawer = ({
  icpData,
  setIcpData,
  getIcpCategoriesMutation,
  attributes,
  isDirty,
  isValid,
  saveAttributes,
  resetDirtyState,
  _attributesLoading,
  onClose,
  _onValidationComplete,
  hasSession = false,
  jobData,
  isFromJobPipeline = false,
  hasValidatedButNotSaved = false,
  // saveIcpMutation, // Commented out - can be uncommented in future
  generateAttributesMutation,
  _isIcpLoading,
  activeStep,
  setActiveStep,
  generatedAttributes,
  onSuccessfulUpdate,
}: UseAiScoringDrawerProps): UseAiScoringDrawerReturn => {
  // Wrapper function for manual step changes
  const setActiveStepManually = useCallback(
    (step: number) => {
      setActiveStep(step);
    },
    [setActiveStep]
  );

  const [footerIsLoading, setFooterIsLoading] = useState(false);
  const [footerLoadingKey, setFooterLoadingKey] = useState<string | null>(null);

  const queryClient = useQueryClient();

  // Track pending actions to handle after API completes
  const pendingActionRef = useRef<string | null>(null);

  // Track if retrigger was requested for pending operations
  const pendingRetriggerRef = useRef<boolean>(false);

  // Track the last successful validation to avoid unnecessary API calls
  const lastValidationSnapshotRef = useRef<string | null>(null);

  // Track if AI test should rerun due to attribute changes
  const [shouldRerunAiTest, setShouldRerunAiTest] = useState(false);

  // Create a snapshot of attributes for comparison
  const createAttributesSnapshot = useCallback((attrs: any[]) => {
    const snapshot = attrs.map((attr) => ({
      description: attr.description.trim(),
      position: attr.position,
    }));
    return JSON.stringify(snapshot);
  }, []);

  const { refetchPipelineInBg, fetchPipelineDependentAPIs } = useJobPipeline();
  const _bulkOperations = useBulkOperationsOptional();

  // Track AI scoring in bulk operation queue
  const aiScoringBulkOp = useBulkOperation({
    activityType: 'ai_scoring',
    title: 'Calculating AI score',
  });

  const refetchData = useCallback(() => {
    if (isFromJobPipeline) {
      refetchPipelineInBg();
      fetchPipelineDependentAPIs();
    }
    queryClient.invalidateQueries({
      queryKey: ['fetchAiScoring', jobData?.id],
    });
  }, [
    isFromJobPipeline,
    refetchPipelineInBg,
    fetchPipelineDependentAPIs,
    jobData?.id,
    queryClient,
  ]);

  // Check if attributes have changed since last successful validation
  const hasAttributesChangedSinceLastValidation = useMemo(() => {
    if (!lastValidationSnapshotRef.current) {
      return true; // No previous validation
    }

    // Parse the last snapshot to compare actual content
    let lastSnapshotData;
    try {
      lastSnapshotData = JSON.parse(lastValidationSnapshotRef.current);
    } catch {
      return true;
    }

    // Create comparison data that ignores the id field (since it can change between saved/unsaved)
    const currentSnapshotData = attributes.map((attr) => ({
      description: attr.description.trim(),
      position: attr.position,
    }));

    const lastSnapshotDataForComparison = lastSnapshotData.map((attr) => ({
      description: attr.description.trim(),
      position: attr.position,
    }));

    // Compare the actual content, ignoring id field
    const hasChanged = !isEqual(
      currentSnapshotData,
      lastSnapshotDataForComparison
    );

    return hasChanged;
  }, [attributes]);

  const toast = useShowToast();

  // Callback to handle validation completion
  const handleValidationComplete = useCallback(
    (result: any) => {
      const pendingAction = pendingActionRef.current;
      const wasRetriggerRequested = pendingRetriggerRef.current;

      // Clear loading states and refs
      setFooterIsLoading(false);
      setFooterLoadingKey(null);
      pendingActionRef.current = null;
      pendingRetriggerRef.current = false;

      // Only proceed if validation was successful
      const isValidationSuccessful =
        result.success &&
        !result.attributes?.some(
          (attr: any) => attr.is_valid === false && attr.valid_type === 'error'
        );

      if (isValidationSuccessful) {
        // Save snapshot of successfully validated attributes
        if (result.attributes) {
          const snapshot = createAttributesSnapshot(result.attributes);
          lastValidationSnapshotRef.current = snapshot;
        }

        // Handle retrigger query invalidation if it was requested
        if (wasRetriggerRequested) {
          queryClient.resetQueries({
            queryKey: ['fetchAiScoring', jobData?.id],
          });
          // Start tracking AI scoring in bulk operation queue and trigger polling
          aiScoringBulkOp.startTracking(1);
          aiScoringBulkOp.completeSuccess(1);
        }

        if (pendingAction === 'next') {
          // Move to next step after successful validation
          setActiveStepManually(STEP_TESTING);

          // Mark that AI test should rerun since attributes were validated
          setShouldRerunAiTest(true);
        } else if (pendingAction === 'skip') {
          // Close drawer after skip and save
          onClose();
          refetchData();
          onSuccessfulUpdate?.();
          // Scroll to auto-reject section after drawer closes
          scrollToElementById('ai-v2-auto-reject-note', toast);
        } else if (pendingAction === 'save') {
          // Reset dirty state after successful save
          resetDirtyState();
          refetchData();
          onSuccessfulUpdate?.();
        }
      }
      // If validation failed, stay on current step/state
    },
    [
      createAttributesSnapshot,
      queryClient,
      jobData?.id,
      setActiveStepManually,
      onClose,
      refetchData,
      onSuccessfulUpdate,
      toast,
      resetDirtyState,
      aiScoringBulkOp,
    ]
  );

  const saveAttributesFromCacheMutation = useMutation({
    mutationFn: ({
      atsJobId,
      retrigger,
    }: {
      atsJobId: string;
      retrigger: boolean;
    }) => aiAttributeApi.saveAttributesFromCache(atsJobId, retrigger),
  });

  // Determine if we have unsaved generated attributes
  // Check if we have freshly generated attributes that haven't been saved to the backend yet
  const hasUnsavedGeneratedAttributes = useMemo(() => {
    // If we have generatedAttributes, they are unsaved by definition
    // (they get cleared once saved or when drawer closes)
    return Boolean(generatedAttributes && generatedAttributes.length > 0);
  }, [generatedAttributes]);

  // Memoize snapshot initialization check
  const shouldInitializeSnapshot = useMemo(() => {
    // Only initialize snapshot if:
    // 1. No existing snapshot
    // 2. Has attributes
    // 3. All attributes are saved (have IDs)
    // 4. All attributes are valid
    const shouldInit =
      !lastValidationSnapshotRef.current &&
      attributes.length > 0 &&
      attributes.every((attr) => typeof attr.id === 'number' && attr.id > 0) && // All saved
      isValid; // All valid

    return shouldInit;
  }, [attributes, isValid]);

  // Initialize snapshot when attributes are valid and saved
  useEffect(() => {
    if (shouldInitializeSnapshot) {
      const snapshot = createAttributesSnapshot(attributes);
      lastValidationSnapshotRef.current = snapshot;
    }
  }, [shouldInitializeSnapshot, attributes, createAttributesSnapshot]);

  // Removed auto-step management - let manual navigation and explicit auto-navigation handle steps

  // REMOVED: All automatic loading state clearing logic to prevent premature clearing

  const handleStepChange = useCallback(
    async (targetStep: number) => {
      // skip if target step is same as active step
      if (targetStep === activeStep) {
        return;
      }

      // for Icp step, no validation needed - allow navigation even during loading
      if (targetStep === STEP_ICP) {
        setActiveStepManually(STEP_ICP);
        // Reset the rerun flag when going back to step 1
        setShouldRerunAiTest(false);
        return;
      }

      // Prevent step changes during loading for other steps (that require validation)
      if (footerIsLoading) {
        return;
      }

      // for attributes step, we need to check if ICP needs saving first
      if (targetStep === STEP_ATTRIBUTES) {
        // If moving from ICP step to Attributes, check if ICP is modified
        if (activeStep === STEP_ICP) {
          const needsIcpValidation = icpData?.isModified;

          if (needsIcpValidation) {
            setFooterIsLoading(true);
            setFooterLoadingKey('verifyIcp');
            pendingActionRef.current = 'verifyIcp';

            try {
              let categoriesToSave = icpData?.categories || [];

              // If ICP is modified, always fetch fresh categories to ensure they match the current ICP content
              // This handles both empty categories and potentially stale categories from before the ICP change
              if (icpData?.data && icpData.data.trim().length > 0) {
                try {
                  const categoriesResult =
                    await getIcpCategoriesMutation.mutateAsync({
                      jobId: jobData.id,
                      icp: icpData.data,
                    });

                  if (categoriesResult?.data?.categories) {
                    categoriesToSave = categoriesResult.data.categories;
                    // Update the categories in state as well
                    setIcpData((prev) => ({
                      ...prev,
                      categories: categoriesToSave,
                    }));
                  }
                } catch {
                  // Continue with save using existing categories if fetch fails
                }
              }

              // OLD LOGIC (commented, can be restored): Save ICP then trigger attribute generation via onSuccess
              // const res = await saveIcpMutation.mutateAsync({
              //   icp: icpData?.data,
              //   categories: categoriesToSave,
              // });
              // if (res?.status === 200) {
              //   // Mark ICP as not modified since it's been saved
              //   setIcpData((prev) => ({
              //     ...prev,
              //     isModified: false,
              //   }));
              //   // Don't clear loading state here - wait for attribute generation to complete
              // }

              // Instead of saving ICP, directly trigger attribute generation
              generateAttributesMutation.mutate(icpData?.data);

              // Mark ICP as not modified to reflect that it has been processed
              setIcpData((prev) => ({
                ...prev,
                isModified: false,
              }));

              // Don't clear loading state here - let attribute generation flow manage it
            } catch {
              // Clear loading state on error
              setFooterIsLoading(false);
              setFooterLoadingKey(null);
              pendingActionRef.current = null;
            }
            // Don't clear loading state in finally block - let the isIcpLoading effect handle it
            return;
          }
        }

        // Check if we need to generate attributes before navigating
        if (Array.isArray(attributes) && attributes.length > 0) {
          // Attributes exist, just navigate
          setActiveStepManually(STEP_ATTRIBUTES);
        } else {
          // No attributes exist, generate them from ICP
          if (icpData?.data?.trim()) {
            generateAttributesMutation.mutate(icpData.data);
            // Navigation will happen automatically when attributes are generated
            // via the fetchAttributesStatusMutation.onSuccess callback
          } else {
            // No ICP data, just navigate and let user manually add attributes
            setActiveStepManually(STEP_ATTRIBUTES);
          }
        }
        return;
      }

      // if we are moving from attribute to testing step, we need to validate attributes
      if (targetStep === STEP_TESTING && activeStep === STEP_ATTRIBUTES) {
        // if attributes are not valid, don't move to testing step
        const needsValidation =
          (isDirty || hasUnsavedGeneratedAttributes) &&
          hasAttributesChangedSinceLastValidation;

        if (needsValidation) {
          setFooterIsLoading(true);
          setFooterLoadingKey('next');
          pendingActionRef.current = 'next';

          try {
            const result = await saveAttributes(false);

            if (result.success) {
              if (result.pending) {
                // Wait for polling to complete - callback will handle the rest
                return;
              } else {
                // For non-pending results, handle immediately
                const hasValidationErrors =
                  !result.success ||
                  (Array.isArray(result.attributes) &&
                    result.attributes.some(
                      (attr) =>
                        attr.is_valid === false && attr.valid_type === 'error'
                    ));

                if (!hasValidationErrors) {
                  // Save snapshot for future comparisons
                  const snapshot = createAttributesSnapshot(attributes);
                  lastValidationSnapshotRef.current = snapshot;
                  setActiveStepManually(STEP_TESTING);

                  // If we have a session, the AiScoreTesting component will auto-start
                  if (hasSession) {
                    setShouldRerunAiTest(true);
                  }
                }
                setFooterIsLoading(false);
                setFooterLoadingKey(null);
                pendingActionRef.current = null;
              }
            } else {
              // Handle failure
              setFooterIsLoading(false);
              setFooterLoadingKey(null);
              pendingActionRef.current = null;
            }
          } catch {
            // Clear loading state on error
            setFooterIsLoading(false);
            setFooterLoadingKey(null);
            pendingActionRef.current = null;
          }
        } else {
          // No validation needed - attributes haven't changed since last successful validation
          setActiveStepManually(STEP_TESTING);
        }
        return;
      }

      // Handle other direct step navigation cases
      if (targetStep === STEP_TESTING) {
        // Allow direct navigation to testing step if attributes are valid
        if (isValid) {
          setActiveStepManually(STEP_TESTING);
        }
      }
    },
    [
      activeStep,
      footerIsLoading,
      setActiveStepManually,
      attributes,
      icpData?.isModified,
      icpData?.categories,
      icpData.data,
      // saveIcpMutation, // Commented out - can be uncommented in future
      getIcpCategoriesMutation,
      jobData.id,
      setIcpData,
      generateAttributesMutation,
      isDirty,
      hasUnsavedGeneratedAttributes,
      hasAttributesChangedSinceLastValidation,
      saveAttributes,
      createAttributesSnapshot,
      hasSession,
      isValid,
    ]
  );

  const handleCancel = useCallback(() => {
    // If there are attributes and none are saved, clear them from the query cache
    if (
      Array.isArray(attributes) &&
      attributes.length > 0 &&
      !attributes.some((attr) => typeof attr.id === 'number' && attr.id > 0)
    ) {
      queryClient.setQueryData(['fetchAiScoring', jobData?.id], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          data: {
            ...old.data,
            data: {
              ...old.data?.data,
              attributes: [],
            },
          },
        };
      });
    }
    onClose();
  }, [attributes, queryClient, jobData?.id, onClose]);

  const handleBack = useCallback(() => {
    const newStep = activeStep - 1;
    setActiveStepManually(newStep);

    // Reset the rerun flag when going back to step 1 (STEP_ICP)
    if (newStep === STEP_ICP) {
      setShouldRerunAiTest(false);
    }
  }, [activeStep, setActiveStepManually]);

  const handleSave = useCallback(
    async (shouldRetrigger: boolean = false) => {
      setFooterIsLoading(true);
      setFooterLoadingKey('save');
      pendingActionRef.current = 'save';

      try {
        const response = await saveAttributesFromCacheMutation.mutateAsync({
          atsJobId: jobData.id,
          retrigger: shouldRetrigger,
        });

        // Check if the response indicates success
        if (response.status === 200) {
          toast({
            title: `Attributes saved successfully`,
          });
          if (shouldRetrigger) {
            // Force a complete cache reset and refetch
            queryClient.resetQueries({
              queryKey: ['fetchAiScoring', jobData?.id],
            });
            // Start tracking AI scoring in bulk operation queue and trigger polling
            aiScoringBulkOp.startTracking(1);
            aiScoringBulkOp.completeSuccess(1);
          }
          onClose();
          refetchData();
          onSuccessfulUpdate?.();
          // Scroll to auto-reject section after drawer closes
          scrollToElementById('ai-v2-auto-reject-note', toast);
        } else if (response.status === 404) {
          toast({
            title: 'Unable to save attributes',
            status: 'message',
          });
        } else {
          // Handle API errors
          // const errorMessage = response.errors?.[0] || 'Unknown error occurred';
          toast({
            title: 'Unable to save attributes',

            status: 'error',
          });
        }
      } catch (error: any) {
        toast({
          title: 'Unable to save attributes',
          description: error?.message || 'Unknown error',
          status: 'error',
        });
      } finally {
        setFooterIsLoading(false);
        setFooterLoadingKey(null);
        pendingActionRef.current = null;
      }
    },
    [
      // The mutation object is only read inside the callback, never compared by
      // reference, so its instability cannot cause a stale closure here.
      // eslint-disable-next-line @tanstack/query/no-unstable-deps
      saveAttributesFromCacheMutation,
      jobData.id,
      toast,
      onClose,
      refetchData,
      onSuccessfulUpdate,
      queryClient,
      aiScoringBulkOp,
    ]
  );

  const handleSkipAndSave = useCallback(
    async (shouldRetrigger: boolean = false) => {
      setFooterIsLoading(true);
      setFooterLoadingKey('skip');
      pendingActionRef.current = 'skip';

      try {
        // Optimization: If attributes have been validated but not saved,
        // we can directly save from cache instead of validating again
        if (hasValidatedButNotSaved) {
          const response = await saveAttributesFromCacheMutation.mutateAsync({
            atsJobId: jobData.id,
            retrigger: shouldRetrigger,
          });

          if (response.status === 200) {
            if (shouldRetrigger) {
              queryClient.resetQueries({
                queryKey: ['fetchAiScoring', jobData?.id],
              });
              // Start tracking AI scoring in bulk operation queue and trigger polling
              aiScoringBulkOp.startTracking(1);
              aiScoringBulkOp.completeSuccess(1);
            }
            onClose();
            refetchData();
            onSuccessfulUpdate?.();
            // Scroll to auto-reject section after drawer closes
            scrollToElementById('ai-v2-auto-reject-note', toast);
          } else if (response.status === 404) {
            toast({
              title: 'Attributes not modified to save',
              status: 'message',
            });
          } else {
            const errorMessage =
              response.errors?.[0] || 'Unknown error occurred';
            toast({
              title: 'Error saving attributes',
              description:
                typeof errorMessage === 'string'
                  ? errorMessage
                  : 'Unknown error',
              status: 'error',
            });
          }

          setFooterIsLoading(false);
          setFooterLoadingKey(null);
          pendingActionRef.current = null;
          return;
        }

        // Original logic for when validation is needed
        const result = await saveAttributes(true, shouldRetrigger);

        if (result.success) {
          // SUCCESS: The API call was successful, but we need to check if it's still processing

          if (result.pending) {
            // PENDING: Operation was successful but is still processing (requires polling)
            // Store retrigger flag so handleValidationComplete can handle it
            pendingRetriggerRef.current = shouldRetrigger;
            // The handleValidationComplete callback will be called when polling completes
            // We keep loading state active and return early to wait for the callback
            return;
          } else {
            // COMPLETED: Operation was successful and completed immediately
            // Handle the completed result right away
            if (isValid) {
              if (shouldRetrigger) {
                // Force a complete cache reset and refetch
                queryClient.resetQueries({
                  queryKey: ['fetchAiScoring', jobData?.id],
                });
                // Start tracking AI scoring in bulk operation queue and trigger polling
                aiScoringBulkOp.startTracking(1);
                aiScoringBulkOp.completeSuccess(1);
              }
              onClose();
              refetchData();
              onSuccessfulUpdate?.();
              // Scroll to auto-reject section after drawer closes
              scrollToElementById('ai-v2-auto-reject-note', toast);
            }
            // Clean up loading states for completed operations
            setFooterIsLoading(false);
            setFooterLoadingKey(null);
            pendingActionRef.current = null;
          }
        } else {
          // FAILED: The API call failed
          // Clean up loading states and stay on current view
          setFooterIsLoading(false);
          setFooterLoadingKey(null);
          pendingActionRef.current = null;
        }
      } catch {
        // EXCEPTION: An error occurred during the API call
        // Clean up loading states and stay on current view
        setFooterIsLoading(false);
        setFooterLoadingKey(null);
        pendingActionRef.current = null;
      }
    },
    [
      hasValidatedButNotSaved,
      saveAttributes,
      // The mutation object is only read inside the callback, never compared by
      // reference, so its instability cannot cause a stale closure here.
      // eslint-disable-next-line @tanstack/query/no-unstable-deps
      saveAttributesFromCacheMutation,
      jobData.id,
      toast,
      onClose,
      refetchData,
      onSuccessfulUpdate,
      queryClient,
      isValid,
      aiScoringBulkOp,
    ]
  );

  const handleVerifyAttributes = useCallback(async () => {
    // Check if we need to validate at all
    const needsValidation =
      (isDirty || hasUnsavedGeneratedAttributes) &&
      hasAttributesChangedSinceLastValidation;

    if (needsValidation) {
      setFooterIsLoading(true);
      setFooterLoadingKey('next');
      pendingActionRef.current = 'next';

      try {
        const result = await saveAttributes(false);

        if (result.success) {
          // SUCCESS: The API call was successful, but we need to check if it's still processing

          if (result.pending) {
            // PENDING: Operation was successful but is still processing (requires polling)
            // The handleValidationComplete callback will be called when polling completes
            // We keep loading state active and return early to wait for the callback
            return;
          } else {
            // COMPLETED: Operation was successful and completed immediately
            // Handle the completed result right away
            if (isValid) {
              // Save snapshot for future comparisons
              const snapshot = createAttributesSnapshot(attributes);
              lastValidationSnapshotRef.current = snapshot;
              setActiveStepManually(STEP_TESTING);

              // Mark that AI test should rerun since attributes were validated
              if (activeStep === STEP_ATTRIBUTES) {
                setShouldRerunAiTest(true);
              }

              // Moving to step 1 with a session: auto-generation is kicked off by
              // the effect watching `activeStep`, so there is nothing to do here.
              // (Empty branches kept rather than removed — the conditions document
              // the state machine and removing them would obscure it.)
              if (activeStep === STEP_ATTRIBUTES && hasSession) {
                // no-op
              }
            } else {
              // no-op
            }
            // Clean up loading states for completed operations
            setFooterIsLoading(false);
            setFooterLoadingKey(null);
            pendingActionRef.current = null;
          }
        } else {
          // FAILED: The API call failed
          // Clean up loading states and stay on current view
          setFooterIsLoading(false);
          setFooterLoadingKey(null);
          pendingActionRef.current = null;
        }
      } catch {
        // EXCEPTION: An error occurred during the API call
        // Clean up loading states and stay on current view
        setFooterIsLoading(false);
        setFooterLoadingKey(null);
        pendingActionRef.current = null;
      }
    } else {
      // No validation needed - attributes haven't changed since last successful validation
      setActiveStep(activeStep + 1);
    }
  }, [
    isDirty,
    hasUnsavedGeneratedAttributes,
    hasAttributesChangedSinceLastValidation,
    saveAttributes,
    isValid,
    createAttributesSnapshot,
    attributes,
    setActiveStepManually,
    activeStep,
    hasSession,
    setActiveStep,
  ]);

  const handleVerifyIcp = useCallback(async () => {
    const needsValidation = icpData?.isModified;

    if (needsValidation) {
      setFooterIsLoading(true);
      setFooterLoadingKey('verifyIcp');
      pendingActionRef.current = 'verifyIcp';

      try {
        let categoriesToSave = icpData?.categories || [];

        // Step 1: Fetch categories
        if (icpData?.data && icpData.data.trim().length > 0) {
          try {
            const categoriesResult = await getIcpCategoriesMutation.mutateAsync(
              {
                jobId: jobData.id,
                icp: icpData.data,
              }
            );

            if (categoriesResult?.data?.categories) {
              categoriesToSave = categoriesResult.data.categories;
              setIcpData((prev) => ({
                ...prev,
                categories: categoriesToSave,
              }));
            }
          } catch {
            // Continue with existing categories if fetch fails
          }
        }

        // OLD LOGIC (commented, can be restored): Save ICP then trigger attribute generation via onSuccess
        // const res = await saveIcpMutation.mutateAsync({
        //   icp: icpData?.data,
        //   categories: categoriesToSave,
        // });
        // if (res?.status === 200) {
        //   setIcpData((prev) => ({
        //     ...prev,
        //     isModified: false,
        //   }));
        //   // Clear loading state - the mutation will handle attribute generation
        //   setFooterIsLoading(false);
        //   setFooterLoadingKey(null);
        //   pendingActionRef.current = null;
        // } else {
        //   setFooterIsLoading(false);
        //   setFooterLoadingKey(null);
        //   pendingActionRef.current = null;
        // }

        // Instead of saving ICP, directly trigger attribute generation
        generateAttributesMutation.mutate(icpData?.data);

        // Mark ICP as not modified to reflect that it has been processed
        setIcpData((prev) => ({
          ...prev,
          isModified: false,
        }));

        // Clear loading state for verify action now that generation is triggered
        setFooterIsLoading(false);
        setFooterLoadingKey(null);
        pendingActionRef.current = null;
      } catch {
        setFooterIsLoading(false);
        setFooterLoadingKey(null);
        pendingActionRef.current = null;
      }
    } else {
      const hasValidExistingAttributes =
        Array.isArray(attributes) &&
        attributes.length > 0 &&
        attributes.some((attr) => attr.description?.trim().length > 0);

      if (hasValidExistingAttributes) {
        setActiveStepManually(STEP_ATTRIBUTES);
      } else if (attributes.length === 0) {
        generateAttributesMutation.mutate(icpData?.data);
      }

      setFooterIsLoading(false);
      setFooterLoadingKey(null);
      pendingActionRef.current = null;
    }
  }, [
    icpData,
    // saveIcpMutation, // Commented out - can be uncommented in future
    generateAttributesMutation,
    setActiveStepManually,
    setIcpData,
    getIcpCategoriesMutation,
    jobData.id,
    attributes,
  ]);

  return {
    // State
    footerIsLoading,
    footerLoadingKey,
    hasUnsavedGeneratedAttributes,
    shouldRerunAiTest,
    hasAttributesChangedSinceLastValidation,

    // Actions
    setFooterIsLoading,
    setFooterLoadingKey,
    setShouldRerunAiTest,

    // Handlers
    handleStepChange,
    handleCancel,
    handleBack,
    handleSave,
    handleSkipAndSave,
    handleVerifyAttributes,
    handleVerifyIcp,
    handleValidationComplete,

    // Utilities
    scrollToElementById,
  };
};
